/**
 * The client status machine.
 *
 * Slice 1 §4: REFERRAL → INTAKE → ACTIVE; ACTIVE ⇄ ON_HOLD; any → DISCHARGED.
 *
 * The rule with teeth is the one about coming back. **A discharged client does
 * not return to active** — that is a new episode of care, with its own start of
 * care date, its own authorizations and its own record. Allowing DISCHARGED →
 * ACTIVE would let an agency reuse one client row across two episodes, and
 * every report that asks "how long was this person on service" would then
 * answer with a span covering the gap.
 *
 * Every transition takes a reason and writes history, which is why the reason
 * is part of the type rather than an optional argument.
 */
export const CLIENT_STATUSES = [
    'REFERRAL',
    'INTAKE',
    'ACTIVE',
    'ON_HOLD',
    'DISCHARGED',
];
/**
 * Legal moves, written out rather than derived.
 *
 * A table is greppable and reviewable; a predicate has to be re-read and
 * re-reasoned every time someone asks "can a client on hold be discharged".
 * Every status appears as a key, including the terminal one, so a reader can
 * see that DISCHARGED has no exits rather than infer it from an absence.
 */
export const CLIENT_STATUS_TRANSITIONS = {
    REFERRAL: ['INTAKE', 'DISCHARGED'],
    INTAKE: ['ACTIVE', 'DISCHARGED'],
    ACTIVE: ['ON_HOLD', 'DISCHARGED'],
    ON_HOLD: ['ACTIVE', 'DISCHARGED'],
    // Terminal. A returning client is a new episode of care, not this row again.
    DISCHARGED: [],
};
export function canTransition(from, to) {
    return CLIENT_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * A result rather than an exception.
 *
 * An illegal transition is something a person did, usually by having two tabs
 * open — it belongs in the response as a `userMessage`, not in a stack trace.
 * The messages below are written for that person: they say what is true now and
 * what can be done instead, and never name a status code or a field.
 */
export function requestTransition(request) {
    const { from, to, reason } = request;
    if (reason.trim() === '') {
        return {
            ok: false,
            error: {
                code: 'REASON_REQUIRED',
                message: 'Say why this client is changing status. It goes on the record with your name.',
            },
        };
    }
    if (from === to) {
        return {
            ok: false,
            error: {
                code: 'SAME_STATUS',
                message: `This client is already ${label(from)}. Nothing was changed.`,
            },
        };
    }
    if (!canTransition(from, to)) {
        return {
            ok: false,
            error: { code: 'ILLEGAL_TRANSITION', message: explain(from, to) },
        };
    }
    return { ok: true, to, reason: reason.trim() };
}
const LABEL = {
    REFERRAL: 'a referral',
    INTAKE: 'in intake',
    ACTIVE: 'active',
    ON_HOLD: 'on hold',
    DISCHARGED: 'discharged',
};
const label = (status) => LABEL[status];
function explain(from, to) {
    if (from === 'DISCHARGED') {
        return ('This client was discharged, and a discharged client does not go back on service. ' +
            'If they are returning, add them as a new client — it is a new episode of care ' +
            'with its own start date and authorizations.');
    }
    const next = CLIENT_STATUS_TRANSITIONS[from];
    return (`A client who is ${label(from)} cannot become ${label(to)}. ` +
        `From here they can only become ${next.map(label).join(' or ')}.`);
}
//# sourceMappingURL=status.js.map